import { useEffect } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { PageShell } from '@/components/layout/PageShell';
import { ApiErrorCard } from '@/components/feedback/ApiErrorCard';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBranchesQuery } from '@/features/branches/queries';
import { ConnectionCard } from '@/features/odoo/components/ConnectionCard';
import { OdooSectionNav } from '@/features/odoo/components/OdooSectionNav';
import { OdooStatusBadge } from '@/features/odoo/components/OdooStatusBadge';
import { ReadinessChecklist } from '@/features/odoo/components/ReadinessChecklist';
import { SettingsForm } from '@/features/odoo/components/SettingsForm';
import { StatusBanner } from '@/features/odoo/components/StatusBanner';
import {
  useOdooPermissions,
  useOdooSettings,
  useOdooSetupCheck,
  useOdooStatus,
  useReconciliationWarningCount,
} from '@/features/odoo/queries';
import { hashFieldTargets, revealOdooField } from '@/features/odoo/field-anchor';
import { odooHostname } from '@/features/odoo/format';
import {
  deriveIntegrationState,
  prerequisiteBlockingCount,
} from '@/features/odoo/status';
import { ClipboardCheck, Info, ListChecks } from '@/lib/icons';

export const Route = createFileRoute('/_layout/settings/odoo/')({
  component: OdooSettingsPage,
});

function OdooSettingsPage() {
  const { t } = useTranslation();
  const { canUpdate } = useOdooPermissions();
  const status = useOdooStatus({ poll: true });
  const settings = useOdooSettings();
  const branches = useBranchesQuery();
  const setupCheck = useOdooSetupCheck();
  const reconciliationWarning = useReconciliationWarningCount();

  const blockingCount = prerequisiteBlockingCount(status.data?.readiness ?? []);
  const abandoned = status.data?.counts.abandoned ?? 0;
  const host = odooHostname(status.data?.odooUrl);

  // Links such as /settings/odoo#odoo-opening-balance arrive before the form has rendered.
  const formReady = Boolean(settings.data) && !branches.isLoading;
  useEffect(() => {
    if (!formReady) return;
    const targets = hashFieldTargets(window.location.hash);
    if (targets.length === 0) return;
    const frame = requestAnimationFrame(() => revealOdooField(targets));
    return () => cancelAnimationFrame(frame);
  }, [formReady]);

  return (
    <PageShell
      title={t('odoo.title')}
      description={t('odoo.description')}
      actions={
        <>
          {status.data ? <OdooStatusBadge status={status.data} /> : null}
          {host ? (
            <span
              className="max-w-[16rem] truncate rounded-full bg-muted px-2.5 py-1 font-mono text-xs text-muted-foreground"
              title={status.data?.odooUrl ?? undefined}
            >
              {host}
            </span>
          ) : null}
          <Button asChild variant="outline" size="sm">
            <Link to="/settings/odoo/sync" search={{ status: 'abandoned' }}>
              <ListChecks className="h-4 w-4" aria-hidden />
              {t('odoo.syncHealth.link')}
              {abandoned > 0 ? (
                <Badge variant="error" size="sm">
                  {abandoned}
                </Badge>
              ) : null}
            </Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/settings/odoo/reconciliation">
              <ClipboardCheck className="h-4 w-4" aria-hidden />
              {t('odoo.reconciliation.link')}
              {reconciliationWarning ? (
                <Badge variant="warning" size="sm">
                  {reconciliationWarning}
                </Badge>
              ) : null}
            </Link>
          </Button>
        </>
      }
    >
      <OdooSectionNav current="settings" />

      {!canUpdate ? (
        <Alert variant="info">
          <Info className="h-4 w-4" aria-hidden />
          <AlertDescription>{t('odoo.readOnlyBanner')}</AlertDescription>
        </Alert>
      ) : null}

      {status.isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : status.isError ? (
        <ApiErrorCard
          error={status.error}
          onRetry={() => void status.refetch()}
          title={t('odoo.status.loadFailed')}
        />
      ) : status.data ? (
        <StatusBanner status={status.data} />
      ) : null}

      <ReadinessChecklist
        checks={status.data?.readiness}
        isLoading={status.isLoading}
        goLive={
          status.data
            ? {
                state: deriveIntegrationState(status.data),
                goLiveAt: status.data.goLiveAt,
              }
            : undefined
        }
        odooUrl={status.data?.odooUrl}
        setupCheck={{
          result: setupCheck.query.data,
          isRunning: setupCheck.query.isFetching,
          error: setupCheck.query.error,
          onRun: setupCheck.canRun ? setupCheck.run : undefined,
        }}
      />

      <ConnectionCard
        status={status.data}
        isLoading={status.isLoading}
        canUpdate={canUpdate}
      />

      {settings.isLoading || branches.isLoading ? (
        <div className="space-y-5" aria-busy="true">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-48 w-full" />
          ))}
        </div>
      ) : settings.isError || !settings.data ? (
        <ApiErrorCard
          error={settings.error}
          onRetry={() => void settings.refetch()}
          title={t('odoo.settings.loadFailed')}
        />
      ) : (
        <SettingsForm
          settings={settings.data}
          branches={branches.data?.value ?? []}
          branchesLoading={false}
          branchesError={branches.isError ? branches.error : null}
          onRetryBranches={() => void branches.refetch()}
          canUpdate={canUpdate}
          blockingCount={blockingCount}
        />
      )}
    </PageShell>
  );
}
